import { features } from '@ui/components/Features';
import type { StatsChartProps } from '@ui/components/StatsCharts/StatsChart';
import { useStatsAverageDailyVolume } from '@ui/hooks/stats/useStatsAverageDailyVolume';
import { useStatsAverageStakeSize } from '@ui/hooks/stats/useStatsAverageStakeSize';
import { useStatsClaimedVno } from '@ui/hooks/stats/useStatsClaimedVno';
import { useStatsDailyActiveWallets } from '@ui/hooks/stats/useStatsDailyActiveWallets';
import { useStatsFountainPenalties } from '@ui/hooks/stats/useStatsFountainPenalties';
import { useStatsFountainTvl } from '@ui/hooks/stats/useStatsFountainTvl';
import { useStatsHelpersByCollection } from '@ui/hooks/stats/useStatsHelpersByCollection';
import { useStatsHelpersByGarden } from '@ui/hooks/stats/useStatsHelpersByGarden';
import { useStatsReservoirTvl } from '@ui/hooks/stats/useStatsReservoirTvl';
import { useStatsRewardsPaid } from '@ui/hooks/stats/useStatsRewardsPaid';
import { useStatsStakers } from '@ui/hooks/stats/useStatsStakers';
import { useStatsStakingTvl } from '@ui/hooks/stats/useStatsStakingTvl';
import { useStatsTvlTokens } from '@ui/hooks/stats/useStatsTvlTokens';
import { useStatsTvlUsd } from '@ui/hooks/stats/useStatsTvlUsd';
import {
  useStatsVnoFountainUnlocked,
  useStatsVnoReservoirUnlocked,
} from '@ui/hooks/stats/useStatsVnoUnlocks';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useTranslations } from '@ui/i18n';
import { endOfDay, startOfDay } from 'date-fns';
import { useDeferredValue, useMemo } from 'react';

export type StatsChartPage = 'vno' | 'staking';

const statsTvlEnabled = isFeatureEnabled(features.statsTvl);

export const useStatsCharts = (
  fromDate: Date,
  toDate: Date,
  page: StatsChartPage,
): StatsChartProps[] => {
  const startTime = Math.floor(startOfDay(fromDate).getTime() / 1000);
  const endTime = Math.floor(endOfDay(toDate).getTime() / 1000);
  const t = useTranslations();

  const claimedVnoChart = useStatsClaimedVno(startTime, endTime);
  const stakersChart = useStatsStakers(startTime, endTime, {
    enabled: page === 'staking',
  });
  const fountainPenaltiesChart = useStatsFountainPenalties(startTime, endTime, {
    enabled: page === 'vno',
  });
  const tvlChart = useStatsTvlUsd(startTime, endTime, {
    enabled: page === 'vno' && statsTvlEnabled,
  });
  const tvlTokensChart = useStatsTvlTokens(startTime, endTime, {
    enabled: page === 'vno' && statsTvlEnabled,
  });
  const reservoirTvlChart = useStatsReservoirTvl(startTime, endTime, {
    enabled: page === 'vno',
  });
  const fountainTvlChart = useStatsFountainTvl(startTime, endTime, {
    enabled: page === 'vno',
  });

  const fountainUnlocksChart = useStatsVnoFountainUnlocked({
    enabled: page === 'vno',
  });
  const reservoirUnlocksChart = useStatsVnoReservoirUnlocked({
    enabled: page === 'vno',
  });
  const stakingTvlChart = useStatsStakingTvl(startTime, endTime, {
    enabled: page === 'staking',
  });
  const rewardsPaidChart = useStatsRewardsPaid(startTime, endTime, {
    enabled: page === 'staking',
  });
  const helpersByCollectionChart = useStatsHelpersByCollection(
    startTime,
    endTime,
    {
      enabled: page === 'vno',
    },
  );
  const averageVolumeChart = useStatsAverageDailyVolume(startTime, endTime, {
    enabled: page === 'staking',
  });
  const activeWalletsChart = useStatsDailyActiveWallets(startTime, endTime, {
    enabled: page === 'staking',
  });
  const stakeSizeChart = useStatsAverageStakeSize(startTime, endTime, {
    enabled: page === 'staking',
  });
  const helpersByGardenChart = useStatsHelpersByGarden(startTime, endTime, {
    enabled: page === 'vno',
  });

  const stats: StatsChartProps[] = useMemo(
    () =>
      page === 'vno' && statsTvlEnabled
        ? [
            claimedVnoChart,
            fountainPenaltiesChart,
            tvlChart,
            tvlTokensChart,
            reservoirTvlChart,
            fountainTvlChart,
            reservoirUnlocksChart,
            fountainUnlocksChart,
            helpersByCollectionChart,
            helpersByGardenChart,
          ]
        : page === 'vno' && !statsTvlEnabled
        ? [
            claimedVnoChart,
            fountainPenaltiesChart,
            reservoirTvlChart,
            fountainTvlChart,
            reservoirUnlocksChart,
            fountainUnlocksChart,
            helpersByCollectionChart,
            helpersByGardenChart,
          ]
        : [
            stakersChart,
            stakingTvlChart,
            rewardsPaidChart,
            averageVolumeChart,
            activeWalletsChart,
            stakeSizeChart,
          ],
    [
      activeWalletsChart,
      averageVolumeChart,
      claimedVnoChart,
      fountainPenaltiesChart,
      fountainTvlChart,
      fountainUnlocksChart,
      helpersByCollectionChart,
      helpersByGardenChart,
      page,
      reservoirTvlChart,
      reservoirUnlocksChart,
      rewardsPaidChart,
      stakeSizeChart,
      stakersChart,
      stakingTvlChart,
      tvlChart,
      tvlTokensChart,
    ],
  );

  return useDeferredValue(stats);
};
